import React, { useCallback, useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, Save, Upload, Layers } from "lucide-react";
import * as Icons from "lucide-react";
import Uploader from "@/components/Uploader";

const LUCIDE_SUGGESTED = ["Wrench", "Flame", "Cog", "Bolt", "Hammer", "HardHat", "Truck", "Cable", "PlugZap", "Building2", "Warehouse", "Cpu", "Drill", "Anvil", "Gauge", "Scale", "Zap"];
const COLORS = ["orange", "blue", "green", "purple", "pink", "yellow", "red"];
const BLANK = { key: "", name_en: "", name_hi: "", name_mr: "", name_ta: "", icon: "Wrench", icon_url: "", color: "orange", order: 100 };

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/categories"); setItems(data); }
    catch (e) { toast.error(formatError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (row) => {
    if (!window.confirm(`Delete category "${row.name_en}"?`)) return;
    try { await api.delete(`/admin/categories/${row.key}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid="admin-categories-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Category Manager</h1>
          <p className="mt-1 text-sm font-medium text-sarthi-muted">Add or remove skill categories. Icons, colours and active-job counts update on the homepage in real time.</p>
        </div>
        <button data-testid="new-cat-btn" onClick={() => setEditing({ ...BLANK })} className="sarthi-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm text-white">
          <Plus className="h-4 w-4" /> New Category
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {items.map((c) => {
          const Icon = Icons[c.icon] || Icons.Wrench;
          return (
            <div key={c.key} data-testid={`cat-card-${c.key}`} className={`sarthi-card p-4 cat-${c.color}`}>
              <div className="flex items-start gap-3">
                {c.icon_url ? (
                  <img src={c.icon_url} alt={c.name_en} className="h-12 w-12 rounded-xl object-cover bg-white/70 p-1" />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/70"><Icon className="h-7 w-7" strokeWidth={2.5} /></div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-display text-base font-extrabold leading-tight">{c.name_en}</div>
                  <div className="text-xs text-sarthi-muted">{c.key}</div>
                  <div className="mt-1 text-xs font-bold">
                    <span className="text-sarthi-primary">{c.active_jobs || 0}</span> jobs · <span className="text-sarthi-secondary">{c.candidates || 0}</span> candidates
                  </div>
                </div>
              </div>
              <div className="mt-3 flex gap-2 text-xs">
                <button data-testid={`edit-${c.key}`} onClick={() => setEditing({ ...c })} className="sarthi-btn-outline inline-flex items-center gap-1 px-2 py-1 font-bold"><Edit3 className="h-3 w-3" /> Edit</button>
                <button data-testid={`del-${c.key}`} onClick={() => del(c)} className="text-red-700 underline"><Trash2 className="inline h-3 w-3" /> Delete</button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="col-span-full sarthi-card p-10 text-center text-sarthi-muted"><Layers className="mx-auto mb-2 h-8 w-8 opacity-50" />No categories yet</div>}
      </div>

      {editing && <EditModal row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EditModal({ row, onClose, onSaved }) {
  const isNew = !row.key || !items_has(row.key);
  const [f, setF] = useState({ ...row });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { ...f, order: Number(f.order) || 100 };
    if (!body.name_en) return toast.error("English name required");
    try {
      if (isNew) {
        if (!body.key) return toast.error("Key required (lowercase-alphanumeric-hyphens)");
        await api.post("/admin/categories", body);
      } else {
        const patch = { ...body }; delete patch.key;
        await api.patch(`/admin/categories/${row.key}`, patch);
      }
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  const PreviewIcon = Icons[f.icon] || Icons.Wrench;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="cat-modal">
      <div className="sarthi-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between"><h2 className="font-display text-xl font-extrabold">{isNew ? "New Category" : `Edit — ${row.name_en}`}</h2><button onClick={onClose}><X className="h-5 w-5" /></button></div>
        <div className="mb-4 flex items-center gap-3 rounded-xl bg-sarthi-accent/40 p-3">
          {f.icon_url ? <img src={f.icon_url} alt="" className="h-14 w-14 rounded-xl object-cover bg-white p-1" /> : <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white"><PreviewIcon className="h-8 w-8" strokeWidth={2.5} /></div>}
          <div><div className="font-display text-lg font-extrabold">{f.name_en || "Preview"}</div><div className="text-xs text-sarthi-muted">Color: {f.color}</div></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {isNew && <input data-testid="cat-key" placeholder="key-lowercase-hyphens" value={f.key} onChange={(e) => setF({ ...f, key: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })} className="col-span-2 rounded-xl border-2 border-sarthi-border px-3 py-2 font-mono" />}
          <input data-testid="cat-name-en" placeholder="Name (EN)" value={f.name_en} onChange={set("name_en")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="cat-name-hi" placeholder="Name (HI)" value={f.name_hi || ""} onChange={set("name_hi")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="cat-name-mr" placeholder="Name (MR)" value={f.name_mr || ""} onChange={set("name_mr")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="cat-name-ta" placeholder="Name (TA)" value={f.name_ta || ""} onChange={set("name_ta")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <select data-testid="cat-icon" value={f.icon || "Wrench"} onChange={set("icon")} className="rounded-xl border-2 border-sarthi-border px-3 py-2">
            {LUCIDE_SUGGESTED.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
          <select data-testid="cat-color" value={f.color || "orange"} onChange={set("color")} className="rounded-xl border-2 border-sarthi-border px-3 py-2">
            {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input data-testid="cat-order" type="number" placeholder="Order" value={f.order || 100} onChange={set("order")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <div className="col-span-2">
            <label className="mb-1 block text-xs font-bold uppercase text-sarthi-muted">Custom icon (overrides lucide)</label>
            <div className="flex gap-2">
              <input data-testid="cat-icon-url" placeholder="Icon image URL (or upload →)" value={f.icon_url || ""} onChange={set("icon_url")} className="flex-1 rounded-xl border-2 border-sarthi-border px-3 py-2 text-xs" />
              <Uploader label="Upload" accept="image/*" onUploaded={(url) => setF((prev) => ({ ...prev, icon_url: url }))} />
            </div>
          </div>
        </div>
        <button data-testid="cat-save" onClick={save} className="sarthi-btn-primary mt-4 inline-flex w-full items-center justify-center gap-2 py-2 text-white"><Save className="h-4 w-4" /> Save</button>
      </div>
    </div>
  );
}

// tiny helper — because we don't want to run a full network query per keystroke
function items_has(k) { return !!k; }
